import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { FormHelper } from '@app/ui-forms/classes/form-helper';
import { DomainHelper } from '@app/core/helpers/domain.helper';

import { ConfigureService } from '@app/configure/services/configure.service';
import { TrackingService } from '@app/core/services/tracking.service';
import { StorageService } from '@app/core/services/storage.service';
import { SessionService } from '@app/core/services/session.service';
import { ModalService } from '@app/core/services/modal.service';
import { ToastService } from '@app/core/services/toast.service';

import { FormInputEvent } from '@app/ui-forms/models/form-input-event.model';
import { FinanceParams } from '@app/configure/models/finance-params.model';
import { Dealer } from '@app/core/models/dealer.model';
import { Modal } from '@app/core/models/modal.model';

import { ValidateBlankText } from '@app/validators/blank-text.validator';

@Component({
	selector: 'cng-customer-details-modal',
	templateUrl: './customer-details.component.html',
	styleUrls: ['./customer-details.component.less'],
})
export class ConfigureCustomerDetailsModalComponent implements OnInit, OnDestroy {
	mode: string;
	vehicle_id: number;
	enquiry_hash: any = this.configure_service.configure_data.meta.enquiry_hash;
	customer: any = this.configure_service.configure_data.meta.customer;
	customer_details_form: FormGroup;
	step_mode: string = this.configure_service.getStepMode();
	reserve_enabled: boolean = this.configure_service.isStepEnabled('payment');
	destroyed$: Subject<boolean> = new Subject<boolean>();
	action_name: string = 'Submit Details';
	loading: boolean = false;
	dealer: Dealer = this.session_service.getDealer();
	tracking_log: string[] = [];
	form_name: string;
	modal_name: string;

	constructor(
		private configure_service: ConfigureService,
		private toast_service: ToastService,
		private activated_route: ActivatedRoute,
		private modal_service: ModalService,
		private router: Router,
		private storage_service: StorageService,
		private tracking_service: TrackingService,
		private session_service: SessionService
	) {
		this.constructModal();
	}

	constructModal(): void {
		if (this.activated_route.params['vehicle_id']) {
			this.mode = 'vehicle';
			this.vehicle_id = this.activated_route.params['vehicle_id'];
		} else {
			this.mode = this.activated_route.params['mode'];
		}

		this.activated_route.params.pipe(takeUntil(this.destroyed$)).subscribe((params: Params) => {
			if (params['vehicle_id']) {
				this.mode = 'vehicle';
				this.vehicle_id = params['vehicle_id'];
				this.form_name = 'configure_customer_capture_form';
				this.modal_name = 'engage_configure_customer_capture_modal';
			} else {
				this.mode = params['mode'];
				this.form_name = 'configure_customer_capture_form';
				this.modal_name = 'engage_configure_customer_capture_modal';
			}

			if (this.mode === 'details') {
				this.action_name = 'Save Details';
				this.form_name = 'configure_customer_details_update_form';
				this.modal_name = 'engage_configure_customer_details_update_modal';
			}

			if (this.mode === 'save') {
				this.action_name = 'Send Link';
				this.form_name = 'configure_save_for_later_form';
				this.modal_name = 'engage_configure_save_for_later_modal';
			}

			this.modal_service.setConfig(
				new Modal({
					name: this.modal_name,
					title: this.mode === 'save' ? 'Save for Later' : 'Your Details',
					height: 'full',
					canDismissWithOverlay: true,
				})
			);
		});

		if (this.mode !== 'vehicle') {
			this.configure_service.track('modal.open', { modal: 'customer.' + this.mode }).subscribe();
		}
	}

	ngOnInit(): void {
		this.buildForm();

		this.configure_service.configure_data$.pipe(takeUntil(this.destroyed$)).subscribe((configure_data) => {
			this.enquiry_hash = configure_data.meta.enquiry_hash;
			this.customer = configure_data.meta.customer;
			this.buildForm();
		});
	}

	buildForm(): void {
		const regex: string = "[A-Za-z-.' ]+";
		const { first_name, last_name, email, tel_mob } = this.customer;

		this.customer_details_form = new FormGroup({
			first_name: new FormControl(first_name, [Validators.required, Validators.pattern(regex), ValidateBlankText]),
			last_name: new FormControl(last_name, [Validators.required, Validators.pattern(regex), ValidateBlankText]),
			email: new FormControl(email === null ? email : email.toLowerCase(), [
				Validators.required,
				Validators.email,
				ValidateBlankText,
			]),
			tel_mob: new FormControl(tel_mob, [Validators.required, Validators.pattern('[0-9 +]+')]),
			terms: new FormControl(this.mode !== 'vehicle', Validators.requiredTrue),
		});
	}

	getFieldInfo(field: string): string {
		if (this.step_mode === 'reserve' || (this.step_mode === 'part_exchange' && this.reserve_enabled)) {
			if (field === 'email') {
				return "We'll send a link to your reservation to this address.";
			}

			if (field === 'tel_mob') {
				return "We'll use this number to contact you about your reservation.";
			}
		}

		if (this.step_mode === 'part_exchange') {
			if (field === 'email') {
				return "We'll send a link to your part exchange valuation to this address.";
			}

			if (field === 'tel_mob') {
				return "We'll use this number to contact you about your part exchange valuation.";
			}
		}

		if (field === 'email') {
			return "We'll send a link to your personalised deal to this address.";
		}

		if (field === 'tel_mob') {
			return "We'll use this number to contact you if you wish to finalise your deal.";
		}
	}

	onSubmit(): void {
		this.tracking_service.trackFormSubmitAttempt(
			this.form_name,
			FormHelper.getCompletedControls(this.customer_details_form),
			FormHelper.getEmptyControls(this.customer_details_form)
		);
		if (this.customer_details_form.invalid) {
			FormHelper.forceValidate(this.customer_details_form);
			this.tracking_service.trackFormValidationFailure(
				this.form_name,
				'invalid_fields[' + FormHelper.getInvalidControls(this.customer_details_form).join(',') + ']'
			);
			this.toast_service.error('Form has invalid fields');
		} else {
			this.tracking_service.trackFormValidationPass(this.form_name);
			this.loading = true;
			const body = {
				customer: {
					first_name: this.customer_details_form.controls['first_name'].value,
					last_name: this.customer_details_form.controls['last_name'].value,
					email: this.customer_details_form.controls['email'].value.toLowerCase().replace(/ /g, ''),
					tel_mob: this.customer_details_form.controls['tel_mob'].value.toLowerCase().replace(/ /g, ''),
				},
				params: this.tracking_service.getParams(),
				step_mode: this.step_mode === 'full' ? null : this.step_mode,
				pex_enquiry_hash: this.configure_service.getPexEnquiryHash(),
			};

			if (this.mode === 'vehicle') {
				//  ENQUIRY DOESN'T YET EXIST, CREATE IT

				const finance_params: FinanceParams = this.session_service.getFinanceParams();

				if (finance_params) {
					body['finance'] = { enabled: true };

					if (finance_params.annual_mileage) {
						body['finance']['mileage'] = finance_params.annual_mileage;
					}

					if (finance_params.deposit) {
						body['finance']['deposit'] = finance_params.deposit;
					}

					if (finance_params.term) {
						body['finance']['term'] = finance_params.term;
					}

					if (finance_params.facility_type) {
						body['finance']['facility_type'] = finance_params.facility_type;
					}
				}

				this.configure_service.createEnquiry(this.vehicle_id, body).subscribe((response: any) => {
					if (response) {
						if (response.token) {
							this.storage_service.setItem(`token-${response.hash}`, response.token);
							this.storage_service.setItem(`vehicle-${this.vehicle_id}`, response.hash);
						}

						this.tracking_service.trackStart();
						this.configure_service.track('enquiry.created', { version: 2 }, response.hash).subscribe();
						this.tracking_service.trackFormSubmitSuccess(this.form_name);
						this.session_service.setFinanceParams(null);

						let route: string[] = [];

						if (this.configure_service.getStepMode() === 'part_exchange') {
							route = ['/configure', 'e', response.hash, 'part-exchange'];
						} else if (this.configure_service.getStepMode() === 'reserve') {
							route = ['/configure', 'e', response.hash, 'payment-options'];
						} else if (this.configure_service.isStepDealerEnabled('part_exchange')) {
							route = ['/configure/e', response.hash, 'part-exchange'];
						} else {
							route = ['/configure/e', response.hash, 'customise-review'];
						}

						this.router.navigate(route).then(() => {
							this.modal_service.close();
						});
					}
				});
			} else {
				const save_for_later_body = {
					customer: body.customer,
					save_for_later: this.mode === 'save',
				};

				this.configure_service.saveCustomer(save_for_later_body, true).subscribe((success: boolean) => {
					if (success) {
						this.loading = false;
						this.modal_service.close();
						this.tracking_service.trackFormSubmitSuccess(this.form_name);
						if (this.mode === 'details') {
							this.toast_service.success('Your details were updated');
							this.tracking_service.trackCustomerDetailsUpdate();
						} else if (this.mode === 'save') {
							this.toast_service.success('Link sent successfully!');
							this.tracking_service.trackSaveForLater();
						}
					}
				});
			}
		}
	}

	onTermsCta(event: any): void {
		if (event === 'terms-and-conditions') {
			window.open(this.dealer.terms_and_conditions_url);
			return;
		}
		window.open(`//${window.location.host}${DomainHelper.getBaseHref()}configure/${event}`, '_blank');
	}

	onFormInput(form: any): void {
		const event: FormInputEvent = FormHelper.getInputEvent(form);
		if (this.tracking_log.indexOf(event.log_name) === -1) {
			this.tracking_service.trackFormInput(event.form_name, event.input_type, event.input_name);
			this.tracking_log.push(event.log_name);
		}
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
	}
}
